import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Container,
    Grid,
    Card,
    Chip,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Avatar,
    TextField,
    InputAdornment,
    IconButton,
    Divider,
    Button
} from '@mui/material';
import { motion } from 'framer-motion';
import {
    Chat as ChatIcon,
    Description as DescriptionIcon,
    AccessTime as AccessTimeIcon,
    Assignment as AssignmentIcon,
    Support as SupportIcon,
    CheckCircle as CheckCircleIcon,
    Psychology as PsychologyIcon,
    Speed as SpeedIcon,
    Groups as GroupsIcon,
    SmartToy as SmartToyIcon,
    Send as SendIcon,
    Phone as PhoneIcon,
    Campaign as CampaignIcon,
    ThumbUpOffAlt as ThumbUpOffAltIcon,
    ThumbDownOffAlt as ThumbDownOffAltIcon,
    Language as LanguageIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { AccountCircle as AccountCircleIcon } from '@mui/icons-material';

const HrAssistant = () => {
    const [chatMessage, setChatMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [chatHistory, setChatHistory] = useState(() => [
        {
            type: 'bot',
            message: 'Merhaba! Ben İK Asistanınızım. Size nasıl yardımcı olabilirim?',
            time: '11:35'
        },
        {
            type: 'user',
            message: 'Yan haklar neler?',
            time: '11:35'
        },
        {
            type: 'bot',
            message: `🎁 **Yan Haklar ve Sosyal Destekler**
- 🍽️ **Yemek Kartı:** Günlük limit şirket politikasına göre belirlenir.
- 🚌 **Servis / Yol Desteği:** Belirli güzergâhlarda servis veya yol katkısı sağlanır.
- 🩺 **Özel Sağlık Sigortası:** Tüm tam zamanlı çalışanlar dahildir; detaylar Portal > Yan Haklar bölümündedir.
- 🎓 **Eğitim ve Gelişim:** Online platformlar ve iç eğitimler yıl içinde duyurulur.

—`,
            time: '11:35',
            feedback: 'like'
        }
    ]);
    const chatBoxRef = useRef(null);
    const isDemoLocked = true; // Giriş kutusunu kilitleyerek demo olduğunu netleştir

    // Sayfa yüklendiğinde en üste scroll
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Demo chat mesajları
    // Hızlı sorular (tıklanabilir)
    const quickQuestions = [
        'Resmî tatiller ne zaman?',
        'İzin nasıl talep ederim?',
        'Uzaktan çalışma kuralları?',
        'Yan haklar neler?',
        'Performans dönemleri?'
    ];

    // Pazarlama vurgusu (tüm yanıtlara eklenecek kısa CTA)
    const marketingSuffix = `—\n**İK Asistanı ile:** daha hızlı yanıt, daha az tekrarlı talep, daha mutlu çalışanlar.\nDetaylı bilgi için: **info@dcat.com.tr** | (0212) 391 01 80`;
    const enhance = (s) => `${s}\n\n${marketingSuffix}`;

    // Gelişmiş kural tabanlı İK chatbot yanıt motoru (react-markdown)
    const getBotResponse = (text) => {
        const t = text.toLowerCase();

        // 1️⃣ İzin & Maaş & Kişisel veriler
        if (
            t.includes("bakiye") ||
            t.includes("yıllık izin") ||
            t.includes("kaç gün") ||
            t.includes("maaş") ||
            t.includes("izin gün")
        ) {
            return enhance(`**Kişisel Veriler Hakkında**
        - Güvenlik nedeniyle kişisel izin bakiyesi, maaş veya bordro detayları sohbet üzerinden paylaşılmaz.
        - Bu bilgilere erişmek için:
            1. **İK Portalı > İzinler** sekmesini açın.
            2. **İK Portalı > Bordro** menüsünden PDF dökümanlara bakın.
        - Görüntüleme sorunu varsa, **İK ekibiyle doğrudan iletişime geçin.**

        Neden bu sınırlama var?
        - **KVKK uyumu** ve gizlilik politikaları
        - Yanlış paylaşımı önlemek için **kimlik doğrulama** şartı.`);
        }

        // 2️⃣ Resmî tatiller 2025
        if (t.includes("tatil") || t.includes("resmî tatil") || t.includes("bayram")) {
            return enhance(`**2025 Resmî Tatil Takvimi (Türkiye)**
    1. **1 Ocak (Çarşamba)** – Yılbaşı
    2. **30 Mart - 1 Nisan (Pazar - Salı)** – Ramazan Bayramı
    3. **23 Nisan (Çarşamba)** – Ulusal Egemenlik ve Çocuk Bayramı
    4. **1 Mayıs (Perşembe)** – Emek ve Dayanışma Günü
    5. **19 Mayıs (Pazartesi)** – Atatürk’ü Anma, Gençlik ve Spor Bayramı
    6. **6 - 9 Haziran (Cuma - Pazartesi)** – Kurban Bayramı
    7. **15 Temmuz (Salı)** – Demokrasi ve Millî Birlik Günü
    8. **30 Ağustos (Cumartesi)** – Zafer Bayramı
    9. **29 Ekim (Çarşamba)** – Cumhuriyet Bayramı`);
        }

        // 3️⃣ İzin talebi adımları
        if (t.includes("izin") && (t.includes("talep") || t.includes("al"))) {
            return enhance(`**İzin Talep Etme Adımları**
    1. **İK Portalı > İzin Talebi** menüsüne giriş yapın.
    2. Uygun **tarih aralığını** ve **izin türünü** seçin (yıllık, mazeret, ücretsiz vb.).
    3. Gerekirse açıklama ekleyin ve **Gönder** butonuna basın.

    Onay süreci:
    - Talebiniz yöneticinize iletilir, onay sonrası size bildirim gelir.
    - Gerekirse **yerine geçecek kişi/iş devri** notu ekleyin.

    İpucu: Onay almadan **uçak/otel** rezervasyonu yapmayın.`);
        }

        // 4️⃣ Uzaktan / Hibrit çalışma politikası
        if (t.includes("uzaktan") || t.includes("hybrid") || t.includes("hibrit") || t.includes("evden")) {
            return enhance(`**Uzaktan / Hibrit Çalışma Politikası**
    - Haftada belirli günler uzaktan çalışma mümkündür.
    - Ofis günleri ekip planına göre değişir (toplantı/işbirliği günleri önceliklidir).
    - Uzaktan çalışırken **verimlilik**, **gizlilik** ve **erişilebilirlik** esastır.
    - Ayrıntılar için: **İK Politikaları > Çalışma Modelleri** sayfasında ki bilgileri aktarmamı ister misiniz?.`);
        }

        // 5️⃣ Yan haklar ve sosyal destekler
        if (
            t.includes("yan hak") ||
            t.includes("yemek") ||
            t.includes("servis") ||
            t.includes("sigorta") ||
            t.includes("kart")
        ) {
            return enhance(`**Yan Haklar ve Sosyal Destekler**
    - **Yemek Kartı:** Günlük limit şirket politikasına göre belirlenir.
    - **Servis / Yol Desteği:** Belirli güzergâhlarda servis veya yol katkısı sağlanır.
    - **Özel Sağlık Sigortası:** Tüm tam zamanlı çalışanlar dahildir; detaylar Portal > Yan Haklar bölümündedir.
    - **Eğitim ve Gelişim:** Online platformlar ve iç eğitimler yıl içinde duyurulur.`);
        }

        // 6️⃣ Performans değerlendirme
        if (t.includes("performans") || t.includes("değerlendirme") || t.includes("geri bildirim")) {
            return enhance(`**Performans Değerlendirme Süreci**
    1. **Hedef Belirleme (Ocak):** Bireysel/ekip hedefleri tanımlanır.
    2. **Ara Değerlendirme (Haziran):** İlerleme ve engeller gözden geçirilir.
    3. **Final Değerlendirme (Aralık):** Sonuçlar ve notlar sistemde kayda alınır.
    4. **Gelişim Planı:** Eğitim ve mentorluk adımları netleştirilir.`);
        }

        // 7️⃣ Politikalar, prosedürler
        if (t.includes("politik") || t.includes("yönetmelik") || t.includes("prosedür") || t.includes("kural")) {
            return enhance(`**Politika ve Prosedürler**
    - Güncel dokümanlar: **İK Portalı > Dokümanlar**
    - En çok arananlar: **Etik Kurallar**, **Uzaktan Çalışma**, **İzin Politikası**, **İSG**
    - Arama kutusuna anahtar kelime yazarak hızlı erişim sağlayabilirsiniz.`);
        }

        // 8️⃣ Bordro
        if (t.includes("bordro") || t.includes("maaş bordrosu") || t.includes("ücret pusulası")) {
            return enhance(`**Bordro Bilgileri**
    - Bordrolar her ayın sonunda **Portal > Bordro** menüsünde PDF olarak yayınlanır.
    - E-posta bildirimlerini aktifleştirerek yeni bordrolardan haberdar olabilirsiniz.
    - Dökümanlar 5 yıl süreyle sistemde arşivlenir.`);
        }

        // 9️⃣ Fazla mesai ve çalışma saatleri
        if (t.includes("fazla mesai") || t.includes("çalışma saati") || t.includes("mesai")) {
            return enhance(`**Çalışma Saatleri ve Fazla Mesai**
    - Standart çalışma saatleri: **Hafta içi 09:00 - 18:00**
    - Fazla mesai, yöneticinizin **ön onayı** ile sisteme kaydedilir.
    - Ücret/denkleştirme uygulamaları bordroya yansıtılır.`);
        }

        // 🔟 Genel cevap
        return enhance(`**Sorunuzu not aldım.**
    İlgili bilgiye Portal üzerinden **Dokümanlar / İK Politikaları / Yan Haklar** menülerinden ulaşabilirsiniz.`    );
    };



    const scheduleBotReply = (text) => {
        const reply = getBotResponse(text);
        const delay = Math.min(2000, Math.max(800, reply.length * 30));
        setIsTyping(true);
        setTimeout(() => {
            const time = new Date().toLocaleTimeString().slice(0, 5);
            setChatHistory((prev) => ([...prev, { type: 'bot', message: reply, time }]));
            setIsTyping(false);
        }, delay);
    };

    const handleQuickQuestion = (msg) => {
        const time = new Date().toLocaleTimeString().slice(0, 5);
        setChatHistory((prev) => ([...prev, { type: 'user', message: msg, time }]));
        scheduleBotReply(msg);
    };

    const handleSendMessage = () => {
        if (chatMessage.trim()) {
            const now = new Date().toLocaleTimeString().slice(0, 5);
            const msg = chatMessage;
            setChatHistory((prev) => ([...prev, { type: 'user', message: msg, time: now }]));
            setChatMessage('');
            scheduleBotReply(msg);
        }
    };

    const handleFeedback = (index, value) => {
        setChatHistory((prev) => {
            const next = [...prev];
            const current = next[index];
            if (!current || current.type !== 'bot') return prev;
            const feedback = current.feedback === value ? undefined : value;
            next[index] = { ...current, feedback };
            return next;
        });
    };

    // Mesaj eklendikçe sohbet kutusunu alta kaydır
    useEffect(() => {
        if (chatBoxRef.current) {
            chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
        }
    }, [chatHistory, isTyping]);

    // Temel özellikler
    const features = [
        {
            icon: <PsychologyIcon />,
            title: 'Sürekli Öğrenme',
            description: 'AI öğrenme sürecinin birçok kaynaktan ve periodik yapılması.'
        },
        {
            icon: <DescriptionIcon />,
            title: 'Dosya Yükleme',
            description: 'PDF, Word, Resim dosyaları yükleme.'
        },
        {
            icon: <GroupsIcon />,
            title: 'Sharepoint Entegrasyonu',
            description: 'Sharepoint Entegrasyonu ile veri alınması.'
        },
        {
            icon: <GroupsIcon />,
            title: 'API Entegrasyonu',
            description: 'API entegrasyonuyla veri alınması.'
        },
        {
            icon: <GroupsIcon />,
            title: 'Portal/Web Entegrasyonu',
            description: 'Web sitesi ya da şirket içi portal’den veri okunması.'
        },
        {
            icon: <CheckCircleIcon />,
            title: 'Aksiyon Alabilme',
            description: 'Aksiyon alınmasının sağlanması. Örneğin, kalan iznini öğrenip istediği tarihe izin alması.'
        },
        {
            icon: <LanguageIcon />,
            title: 'Global Destek',
            description: 'Çoklu dil desteği ile global ekiplere cevap verilebilmesi.'
        },
        {
            icon: <GroupsIcon />,
            title: 'Kurumsal Uyum',
            description: 'Şirket kültürüne ve ihtiyaçlarına göre uyarlanabilir cevap verme.'
        },
        {
            icon: <AssignmentIcon />,
            title: 'Soru Yönetimi',
            description: 'Hazır soruların tanımlanabilmesi, yönetilebilmesi.'
        },
        {
            icon: <PhoneIcon />,
            title: 'Mobil Uyumluluk',
            description: 'Mobil uyumlu kullanım.'
        },
        {
            icon: <ThumbUpOffAltIcon />,
            title: 'Geri Bildirim',
            description: 'Cevaplar için “faydalı/faydalı değil” bilgisinin alınması, raporlanabilmesi.'
        },
        {
            icon: <SpeedIcon />,
            title: 'Analiz ve Raporlama',
            description: 'Kullanımların raporlanabilmesi, analiz edilebilmesi, İK Asistanının sürekli geliştirilmesi.'
        }
    ];

    // Kullanım senaryoları
    const useCases = [
        {
            title: 'Kurumsal Politikalar',
            icon: <DescriptionIcon />,
            items: [
                'Çalışma saatleri ve düzeni',
                'Uzaktan/Hibrit çalışma kuralları',
                'İş sağlığı ve güvenliği protokolleri',
                'Şirket içi güncel yönetmelikler'
            ]
        },
        {
            title: 'İzin ve Tatil Süreçleri',
            icon: <AssignmentIcon />,
            items: [
                'Resmî tatil ve bayram takvimi',
                'Yıllık ve mazeret izni prosedürleri',
                'İzin talep ve onay adımları',
                'Farklı izin türleri hakkında bilgi'
            ]
        },
        {
            title: 'Bordro & Yan Haklar',
            icon: <SupportIcon />,
            items: [
                'Maaş ödeme tarihleri',
                'Fazla mesai uygulamaları',
                'Özel sağlık sigortası kapsamı',
                'Yemek & servis destekleri'
            ]
        },
        {
            title: 'Performans & Geri Bildirim',
            icon: <PsychologyIcon />,
            items: [
                'Hedef belirleme dönemleri',
                'Ara değerlendirme süreçleri',
                'Puanlama ve yetkinlikler',
                'Geri bildirim kültürü'
            ]
        }
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    color: 'white',
                    py: { xs: 8, md: 12 },
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <Chip
                                    label="Yapay Zeka Destekli İK Çözümü"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        mb: 2,
                                        fontWeight: 600
                                    }}
                                />
                                <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                                    İnsan Kaynakları Dijital Asistanı
                                </Typography>
                                <Typography variant="h5" sx={{ mb: 3, opacity: 0.95, lineHeight: 1.6 }}>
                                    Rutin soruların İK asistan tarafından cevaplanarak 7/24 hizmet sunulması, hem çalışan memnunyetinin sağlanması hem de İK iş yükünün hafifletilmesini sağlayan yapay zeka dijital asistanı
                                </Typography>

                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                {/* Demo Chat Interface */}
                                <Paper sx={{ p: 3, borderRadius: 3, maxWidth: 400, mx: 'auto', position: 'relative' }}>
                                    <Chip label="Örnek" size="small" sx={{ position: 'absolute', top: 8, right: 8, bgcolor: '#e0e0e0', color: '#424242', fontWeight: 600 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <SmartToyIcon sx={{ color: '#1e3c72', mr: 1 }} />
                                        <Typography variant="h6" sx={{ color: '#1e3c72', fontWeight: 600 }}>
                                            İK Asistanı
                                        </Typography>
                                    </Box>
                                    {/* Mesajlar alanı (Height increased for longer view) */}
                                    <Box sx={{ height: 450, overflowY: 'auto', mb: 2 }} ref={chatBoxRef}>
                                        {chatHistory.map((msg, index) => (
                                            <Box key={index} sx={{ mb: 1 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                                                        alignItems: 'flex-end',
                                                        gap: 1
                                                    }}
                                                >
                                                    {msg.type !== 'user' && (
                                                        <Avatar sx={{ bgcolor: '#1e3c72', width: 28, height: 28 }}>
                                                            <SmartToyIcon fontSize="small" />
                                                        </Avatar>
                                                    )}
                                                    <Paper
                                                        sx={{
                                                            p: 1.5,
                                                            display: 'inline-block',
                                                            maxWidth: '80%',
                                                            bgcolor: msg.type === 'user' ? '#1e3c72' : '#f5f5f5',
                                                            color: msg.type === 'user' ? 'white' : 'black',
                                                            borderRadius: msg.type === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                                            overflowWrap: 'anywhere',
                                                            wordBreak: 'break-word'
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{
                                                            whiteSpace: 'pre-wrap',
                                                            lineHeight: 1.6,
                                                            overflowWrap: 'anywhere',
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {msg.message}
                                                        </Typography>
                                                    </Paper>
                                                    {msg.type === 'user' && (
                                                        <Avatar sx={{ bgcolor: '#2a5298', width: 28, height: 28 }}>
                                                            <AccountCircleIcon fontSize="small" />
                                                        </Avatar>
                                                    )}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        mt: 0.5,
                                                        gap: 0.5,
                                                        justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start'
                                                    }}
                                                >
                                                    <Typography variant="caption" sx={{ opacity: 0.7 }}>{msg.time}</Typography>
                                                    {msg.type === 'bot' && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                                                            <IconButton size="small" onClick={() => handleFeedback(index, 'like')}>
                                                                <ThumbUpOffAltIcon fontSize="inherit" color={msg.feedback === 'like' ? 'primary' : 'inherit'} />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => handleFeedback(index, 'dislike')}>
                                                                <ThumbDownOffAltIcon fontSize="inherit" color={msg.feedback === 'dislike' ? 'error' : 'inherit'} />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        ))}
                                        {isTyping && (
                                            <Box sx={{ mb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                                                    <Avatar sx={{ bgcolor: '#1e3c72', width: 28, height: 28 }}>
                                                        <SmartToyIcon fontSize="small" />
                                                    </Avatar>
                                                    <Paper sx={{ p: 1.5, display: 'inline-block', bgcolor: '#f5f5f5', color: 'black', borderRadius: '16px 16px 16px 4px' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                                            <Box component={motion.div} animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }} transition={{ duration: 0.9, repeat: Infinity }} sx={{ width: 6, height: 6, bgcolor: '#9e9e9e', borderRadius: '50%' }} />
                                                            <Box component={motion.div} animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }} transition={{ duration: 0.9, repeat: Infinity, delay: 0.2 }} sx={{ width: 6, height: 6, bgcolor: '#9e9e9e', borderRadius: '50%' }} />
                                                            <Box component={motion.div} animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }} transition={{ duration: 0.9, repeat: Infinity, delay: 0.4 }} sx={{ width: 6, height: 6, bgcolor: '#9e9e9e', borderRadius: '50%' }} />
                                                        </Box>
                                                    </Paper>
                                                </Box>
                                                <Typography variant="caption" sx={{ opacity: 0.7, mt: 0.5 }}>Yazıyor…</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    {/* Hızlı sorular: mesajların altında, girişin üstünde yatay scroll */}
                                    <Box sx={{
                                        display: 'flex',
                                        gap: 1,
                                        mb: 1.25,
                                        overflowX: 'auto',
                                        overflowY: 'hidden',
                                        whiteSpace: 'nowrap',
                                        px: 0.5,
                                        '&::-webkit-scrollbar': { height: 6 },
                                        '&::-webkit-scrollbar-thumb': { backgroundColor: '#b5b5b5', borderRadius: 8 }
                                    }}>
                                        {quickQuestions.map((msg, index) => (
                                            <Chip
                                                key={index}
                                                label={msg}
                                                size="small"
                                                clickable
                                                onClick={() => handleQuickQuestion(msg)}
                                                sx={{
                                                    fontSize: '0.9rem',
                                                    mr: 0.5
                                                }}
                                            />
                                        ))}
                                    </Box>
                                    <TextField
                                        value={chatMessage}
                                        onChange={(e) => setChatMessage(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSendMessage(); }}
                                        placeholder={isDemoLocked ? 'Örnek demo - giriş devre dışı' : 'Mesajınızı yazın...'}
                                        size="small"
                                        fullWidth
                                        disabled={isDemoLocked}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleSendMessage} aria-label="mesaj gönder" disabled={isDemoLocked}>
                                                        <SendIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        Bu asistandaki yanıtlar örnek amaçlıdır.
                                    </Typography>

                                </Paper>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Örnek Proje / Başarı Hikayesi - taşındı: CTA'nın altına alınacak */}

            {/* Özellikler */}
            <Box sx={{ bgcolor: 'white', py: 8 }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={6}>
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                            Sistemin Temel Özellikleri
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                            İK süreçlerinizi dijitalleştiren ve çalışan deneyimini iyileştiren özellikler
                        </Typography>
                    </Box>
                    <Grid container spacing={4}>
                        {features.map((feature, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                >
                                    <Card sx={{ p: 3, height: '100%', '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }, transition: 'all 0.3s' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
                                                {feature.icon}
                                            </Avatar>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                {feature.title}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                            {feature.description}
                                        </Typography>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Kullanım Senaryoları */}
            <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={6}>
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                            Asistanın Yanıtlayabildiği Konular
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            Çalışanlarınız aşağıdaki tüm İK süreçleri hakkında saniyeler içinde doğru bilgiye ulaşabilir
                        </Typography>
                    </Box>
                    <Grid container spacing={4}>
                        {useCases.map((useCase, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                >
                                    <Card sx={{ p: 3, height: '100%', bgcolor: 'white', '&:hover': { boxShadow: 6 }, transition: 'all 0.3s' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Avatar sx={{ bgcolor: 'primary.main', mr: 1.5 }}>
                                                {useCase.icon}
                                            </Avatar>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>
                                                {useCase.title}
                                            </Typography>
                                        </Box>
                                        <List dense>
                                            {useCase.items.map((item, itemIndex) => (
                                                <ListItem key={itemIndex} sx={{ px: 0, py: 0.5 }}>
                                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                                        <CheckCircleIcon color="success" fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={item}
                                                        primaryTypographyProps={{ variant: 'body2' }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Nasıl Çalışır */}
            <Box sx={{ bgcolor: 'white', py: 8 }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={6}>
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                            Nasıl Çalışır?
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            3 basit adımda İK asistanınız hazır
                        </Typography>
                    </Box>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true }}
                            >
                                <Card sx={{ p: 4, textAlign: 'center', height: '100%' }}>
                                    <Avatar sx={{ bgcolor: '#1e3c72', width: 72, height: 72, mx: 'auto', mb: 3, fontSize: '2rem' }}>
                                        1
                                    </Avatar>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                                        Dokümanları Yükleyin
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        İK politikalarınızı, prosedürlerinizi, yönetmeliklerinizi ve diğer İK dokümanlarınızı sisteme yükleyin.
                                    </Typography>
                                </Card>
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                viewport={{ once: true }}
                            >
                                <Card sx={{ p: 4, textAlign: 'center', height: '100%' }}>
                                    <Avatar sx={{ bgcolor: '#2a5298', width: 72, height: 72, mx: 'auto', mb: 3, fontSize: '2rem' }}>
                                        2
                                    </Avatar>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                                        Yapay Zeka Öğrenir
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        Sistem dokümanlarınızı analiz eder ve firmaya özel bilgi tabanınızı oluşturur.
                                    </Typography>
                                </Card>
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                                viewport={{ once: true }}
                            >
                                <Card sx={{ p: 4, textAlign: 'center', height: '100%' }}>
                                    <Avatar sx={{ bgcolor: '#1e3c72', width: 72, height: 72, mx: 'auto', mb: 3, fontSize: '2rem' }}>
                                        3
                                    </Avatar>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                                        Çalışanlar Kullanır
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        Çalışanlarınız 7/24 İK sorularını sorar, anında doğru yanıtlar alır.
                                    </Typography>
                                </Card>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>



            {/* Ücretsiz Demo Talep Et CTA */}
            <Box
                component={motion.div}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                sx={{
                    py: { xs: 8, md: 12 },
                    background: 'linear-gradient(180deg, #0b1426 0%, #0a1020 100%)',
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            color: '#ffffff',
                            mb: 3,
                            fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                            textShadow: '2px 0 0 rgba(255,60,80,0.55), -2px 0 0 rgba(60,170,255,0.55)',
                            letterSpacing: '-0.5px'
                        }}
                    >
                        Ücretsiz Demo Talep Et
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.72)',
                            maxWidth: 720,
                            mx: 'auto',
                            mb: 5,
                            fontSize: { xs: '1rem', md: '1.125rem' },
                            lineHeight: 1.7
                        }}
                    >
                        Yapay zeka çözümlerimizin işletmenize katacağı değeri birlikte keşfedelim. Hemen bizimle iletişime geçin ve uzman ekibimizden demo talep edin.
                    </Typography>
                    <Button
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => window.open('https://dcat.com.tr/classic/tr/iletisim', '_blank')}
                        sx={{
                            px: 5,
                            py: 1.75,
                            fontSize: '1.05rem',
                            fontWeight: 700,
                            color: '#fff',
                            borderRadius: 999,
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, #3b5cff 0%, #6a4cff 100%)',
                            boxShadow: '0 0 40px rgba(79, 92, 255, 0.55), 0 8px 24px rgba(0,0,0,0.35)',
                            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #4a6bff 0%, #7a5cff 100%)',
                                boxShadow: '0 0 55px rgba(106, 76, 255, 0.7), 0 10px 28px rgba(0,0,0,0.4)',
                                transform: 'translateY(-2px)'
                            }
                        }}
                    >
                        Hemen Başlayın
                    </Button>
                </Container>
            </Box>


        </Box>
    );
};

export default HrAssistant;